/*
	When making certain transitions in the UI, we want to clear out certain active filters
	that we get from the Filtering and ColumnFilters reducer packages.

	e.g. We want to preserve filtering on the Creatives, Assets and Previews grids, until we
	load a new library, and then we want to reset all of these filters all at once.

	You can do this by calling the function returned by this hook.

	The hook takes a `reducers` argument, which is essentially the hash of action creators for each reducer,
	which should have a `resetAllFilters` and `clearColumnFilters` action creator.  We typically have these
	exported from their respective reducer files as a named export, matching the name of the file.

	Example usage:
	```
		import { Creatives } from "reducers/Creatives";
		import { Assets } from "reducers/Assets";
		...

		function OpenLibraryAction({ libraryId }){
			const clearFilters = useClearFilters([Creatives, Assets]);

			return <LinkAction path={`/libraries/${libraryId}`} onClick={clearFilters} />;
		}
	```
*/

import { useDispatch } from "react-redux";
import { Assets } from "reducers/Assets";
import { CreativePreviews } from "reducers/CreativePreviews";
import { Creatives } from "reducers/Creatives";
import { CreativeVersions } from "reducers/CreativeVersions";

export default function useClearFilters(reducers = []){
	const dispatch = useDispatch();
	return function clearFilters(){
		reducers.forEach((reducer) => {
			// resetAllFilters clears top level filters like search terms, current page, sort order, advanced search, etc.
			const resetAll = reducer?.resetAllFilters?.();
			if(resetAll){
				dispatch(resetAll);
			} else {
				console.warn("`resetAllFilters` action creator not found for provided reducer");
			}

			// clearColumnFilters clears column specific filters like checkbox types, number ranges, name filters, etc.
			const clearColumnFilters = reducer?.clearColumnFilters?.();
			if(clearColumnFilters){
				dispatch(clearColumnFilters);
			} else {
				console.warn("`clearColumnFilters` action creator not found for provided reducer");
			}
		});
	};
}

/*
	For convenience, we also export a hook for the standard set of things to reset for a library change
*/
export function useClearLibraryFilters(){
	return useClearFilters([
		Creatives,
		CreativeVersions,
		Assets,
		CreativePreviews
	]);
}
